import React, { useState, useContext, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { Sidebar, Menu, MenuItem } from 'react-pro-sidebar';
import {
  FaTools,
  FaChalkboardTeacher,
  FaArrowLeft,
  FaUserCircle,
  FaBars,
  FaUserShield,
  FaSignOutAlt,
  FaHome,
  FaCalendarAlt,
  FaTicketAlt,
  FaSchool,
  FaMailBulk,
  FaUtensils,
  FaShieldAlt,
  FaRobot,
  FaClipboardCheck
} from 'react-icons/fa';

import Management from './Management/JSX/Management';
import ClassesComponent from './Devices/JSX/ClassesComponent';
import ProfileComponent from './Profile/ProfileComponent';
import AdminPanel from './Admin/JSX/AdminPanel';
import Inicio from './Inicio';
import Reservas from './Reservas/JSX/Reservas';
import TicketSystem from './Incidencias/JSX/TicketSystem';
import TicketUserSystem from './Incidencias/JSX/TicketUserSystem';
import Gmail from './Workspace/JSX/Gmail';
import { AuthContext } from './AuthContext';
import { SocketProvider } from './SocketContext';
import {
  PermissionProvider,
  usePermissions,
  ProtectedRoute,
  PermissionGate,
  useDepartmentPermissions,
  useUserPermissionsGranular
} from './Admin/JSX/PermissionSystem';
import { db, doc, getDoc } from './firebase';
import styles from './App.module.css';

// Hook personalizado para detectar dispositivos móviles
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 850);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 850);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return isMobile;
};

// ✅ ROLES HARDCODEADOS DEL SISTEMA
const SYSTEM_ROLES = {
  admin: {
    id: 'admin',
    name: 'Administrador',
    description: 'Acceso completo a todas las funcionalidades del sistema',
    permissions: ['gestion', 'correos', 'clases', 'reservas', 'incidencias', 'admin'],
    emailPermissions: {
      allowedOUs: [],
      canManageAll: true
    },
    classPermissions: {
      allowedGroups: [],
      canManageAll: true
    },
    reservationPermissions: {
      advancedPermissions: ['createSpaces', 'editSpaces', 'deleteSpaces', 'createPeriodicReservations']
    }
  },
  user: {
    id: 'user',
    name: 'Usuario',
    description: 'Acceso básico solo a Reservas e Incidencias (sin permisos avanzados)',
    permissions: ['reservas', 'incidencias'],
    emailPermissions: {
      allowedOUs: [],
      canManageAll: false
    },
    classPermissions: {
      allowedGroups: [],
      canManageAll: false
    },
    reservationPermissions: {
      advancedPermissions: []
    }
  }
};

// Hook personalizado para cargar rol de usuario
const useUserRole = (user) => {
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const loadUserRole = async () => {
      if (!user?.role) {
        setUserRole(null);
        setLoading(false);
        return;
      }

      // ✅ Verificar si es un rol del sistema (admin o user)
      if (SYSTEM_ROLES[user.role]) {
        if (isMounted) {
          console.log('✅ Rol del sistema cargado:', user.role);
          setUserRole(SYSTEM_ROLES[user.role]);
          setLoading(false);
        }
        return;
      }

      // ✅ Si no es rol del sistema, buscar en Firestore (roles personalizados)
      try {
        const roleDoc = await getDoc(doc(db, 'roles', user.role));
        
        if (isMounted) {
          if (roleDoc.exists()) {
            console.log('✅ Rol personalizado cargado:', user.role);
            setUserRole({ id: roleDoc.id, ...roleDoc.data() });
          } else {
            console.warn('⚠️ Rol no encontrado en Firestore:', user.role);
            setUserRole(null);
          }
          setLoading(false);
        }
      } catch (error) {
        console.error('❌ Error cargando rol:', error);
        if (isMounted) {
          setUserRole(null);
          setLoading(false);
        }
      }
    };
    
    loadUserRole();

    return () => {
      isMounted = false;
    };
  }, [user?.role]);

  return { userRole, loading };
};

// Hook para manejar el estado de la sidebar de forma inteligente
const useSidebarState = (isMobile) => {
  // Leer estado guardado o usar default (expandida en desktop, colapsada en mobile)
  const getInitialState = () => {
    if (isMobile) return false;
    const saved = localStorage.getItem('sidebarCollapsed');
    return saved === 'true';
  };

  const [collapsed, setCollapsed] = useState(getInitialState);
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  // Guardar preferencia del usuario
  const toggleCollapsed = useCallback(() => {
    setCollapsed(prev => {
      const newState = !prev;
      if (!isMobile) {
        localStorage.setItem('sidebarCollapsed', newState.toString());
      }
      return newState;
    });
  }, [isMobile]);

  // Auto-colapsar en mobile cuando cambia de vista
  useEffect(() => {
    if (isMobile) {
      setShowMobileMenu(false);
    }
  }, [isMobile]);

  // Restablecer estado al cambiar entre mobile/desktop
  useEffect(() => {
    if (isMobile) {
      setCollapsed(false);
    } else {
      const saved = localStorage.getItem('sidebarCollapsed');
      setCollapsed(saved === 'true');
    }
  }, [isMobile]);

  return {
    collapsed,
    showMobileMenu,
    setShowMobileMenu,
    toggleCollapsed
  };
};

// Componente de la barra lateral
const AppSidebar = React.memo(({
  collapsed,
  isMobile,
  showMobileMenu,
  activeComponent,
  menuItems,
  proximamenteItems,
  onToggleCollapse,
  onNavigate,
  onWebsiteClick,
  onProximamenteClick
}) => {
  // Construcción de clases con lógica clara
  const sidebarClasses = [
    styles.sidebar,
    isMobile && styles.mobileSidebar,
    isMobile && showMobileMenu && styles.showMobileSidebar
  ].filter(Boolean).join(' ');

  // Estilos inline - simplificados ya que el wrapper controla la posición en móvil
  const rootStyles = {
    height: '100vh',
    width: isMobile ? '280px' : undefined,
    position: isMobile ? 'relative' : undefined,
    zIndex: isMobile ? 'auto' : 100
  };

  // Wrapper para móvil con control total de posición
  const sidebarContent = (
    <Sidebar
      collapsed={isMobile ? false : collapsed}
      className={sidebarClasses}
      rootStyles={rootStyles}
    >
    <div className={`${styles.sidebarHeader} ${!isMobile && collapsed ? styles.headerCollapsed : ''}`}>
      <div className={styles.logoContainer}>
        <img src="/logoapp.png" alt="Logo" className={styles.logoImg} />
        {(!collapsed || isMobile) && <span className={styles.logoText}>Trueba Virtual</span>}
      </div>
      {!isMobile && (
        <FaBars
          className={styles.menuToggleIcon}
          onClick={onToggleCollapse}
          title={collapsed ? "Expandir menú" : "Contraer menú"}
        />
      )}
    </div>
    
    <Menu className={styles.sidebarMenu}>
      {menuItems.map(item => (
        <PermissionGate key={item.id} permission={item.permission}>
          <MenuItem 
            icon={item.icon}
            onClick={() => onNavigate(item.component)}
            className={`${styles.menuItem} ${activeComponent === item.component ? styles.activeMenuItem : ''}`}
          >
            {item.name}
          </MenuItem>
        </PermissionGate>
      ))}

      {proximamenteItems.map(item => (
        <MenuItem 
          key={item.id}
          icon={item.icon}
          onClick={() => onProximamenteClick(item.feature)}
          className={`${styles.menuItem} ${styles.proximamenteItem}`}
          style={{ opacity: 0.7, fontStyle: 'italic' }}
        >
          {item.name}
        </MenuItem>
      ))}

      <MenuItem
        icon={<FaSchool />}
        onClick={onWebsiteClick}
        className={`${styles.menuItem} ${styles.externalLink}`}
      >
        Txosten Orokorra
      </MenuItem>
      
      {activeComponent !== 'inicio' && (
        <MenuItem
          icon={<FaArrowLeft />}
          onClick={() => onNavigate('inicio')}
          className={`${styles.menuItem} ${styles.backButton}`}
        >
          Volver
        </MenuItem>
      )}
    </Menu>
  </Sidebar>
  );

  // Si es móvil, envolver en un div con control absoluto de posición
  if (isMobile) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: showMobileMenu ? 0 : -280,
        width: 280,
        height: '100vh',
        zIndex: 1999,
        transition: 'left 0.4s ease',
        backgroundColor: 'transparent'
      }}>
        {sidebarContent}
      </div>
    );
  }

  return sidebarContent;
});

// Componente de la barra superior
const TopBar = React.memo(({
  title,
  collapsed,
  isMobile,
  showMobileMenu,
  onProfileClick,
  onLogout,
  onToggleMobileMenu
}) => (
  <header className={`${styles.topBar} ${!isMobile && collapsed ? styles.topBarCollapsed : !isMobile ? styles.topBarExpanded : ''}`}>
    {isMobile && !showMobileMenu && (
      <button
        className={styles.mobileMenuButton}
        onClick={onToggleMobileMenu}
        aria-label="Menú"
      >
        <FaBars />
      </button>
    )}
    <div className={styles.topBarTitle}>
      <h1>{title}</h1>
    </div>
    <div className={styles.topBarIcons}>
      <FaUserCircle
        className={styles.icon}
        title="Perfil"
        onClick={onProfileClick}
      />
      <button
        onClick={onLogout}
        className={styles.logoutButton}
        aria-label="Cerrar sesión"
      >
        <FaSignOutAlt className={styles.icon} title="Cerrar sesión" />
      </button>
    </div>
  </header>
));

// Componente del Footer
const Footer = React.memo(({ collapsed, isMobile }) => (
  <footer className={`${styles.footer} ${!isMobile && collapsed ? styles.footerCollapsed : !isMobile ? styles.footerExpanded : ''}`}>
    <div className={styles.footerContent}>
      <p>&copy; {new Date().getFullYear()} Colegio Trueba - Trueba Virtual. Todos los derechos reservados.</p>
    </div>
  </footer>
));

// Componente principal de la aplicación
const AppContent = () => {
  const { user, setUser } = useContext(AuthContext);
  const { hasPermission, isAdmin, loading: permissionsLoading } = usePermissions();
  const { isAdminDepartamento, checkAdminDepartamento } = useDepartmentPermissions();
  const { granularPermissions, loading: granularLoading } = useUserPermissionsGranular();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const { userRole, loading: roleLoading } = useUserRole(user);
  
  // Usar el hook inteligente de sidebar
  const { collapsed, showMobileMenu, setShowMobileMenu, toggleCollapsed } = useSidebarState(isMobile);

  const sidebarRef = useRef(null);
  const topBarRef = useRef(null);

  // Verificar admin de departamento
  useEffect(() => {
    if (user?.email) {
      checkAdminDepartamento(user.email);
    }
  }, [user?.email, checkAdminDepartamento]);

  // Click fuera de la barra lateral
  useEffect(() => {
    if (collapsed || isMobile) return;

    const handleClickOutside = (event) => {
      const clickedSidebar = sidebarRef.current?.contains(event.target);
      const clickedToggleButton = event.target.closest(`.${styles.menuToggleIcon}`);
      
      if (clickedSidebar || clickedToggleButton) {
        return;
      }

      toggleCollapsed();
    };

    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [collapsed, isMobile, toggleCollapsed]);

  // Obtener componente activo
  const activeComponent = useMemo(() => 
    location.pathname.slice(1).split('/')[0] || 'inicio',
    [location.pathname]
  );

  // Obtener título del componente
  const componentTitle = useMemo(() => {
    const titles = {
      gestion: 'Gestión',
      clases: 'Clases',
      reservas: 'Reservas',
      incidencias: 'Incidencias',
      correos: 'Gestión de Correos',
      perfil: 'Perfil',
      admin: 'Panel de Administración',
      inicio: 'Inicio'
    };
    return titles[activeComponent] || 'Inicio';
  }, [activeComponent]);

  // Callbacks memorizados
  const handleNavigate = useCallback((component) => {
    navigate(`/${component}`);
    if (isMobile) setShowMobileMenu(false);
  }, [navigate, isMobile, setShowMobileMenu]);

  const handleWebsiteClick = useCallback(() => {
    window.open('https://sites.google.com/colegiotrueba.net/txostenorokorratrueba/inicio', '_blank', 'noopener,noreferrer');
    if (isMobile) setShowMobileMenu(false);
  }, [isMobile, setShowMobileMenu]);

  const handleProximamenteClick = useCallback((feature) => {
    alert(`${feature} estará disponible próximamente`);
    if (isMobile) setShowMobileMenu(false);
  }, [isMobile, setShowMobileMenu]);

  const handleLogout = useCallback(() => {
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    localStorage.removeItem('sidebarCollapsed');
    setUser(null);
    navigate('/');
  }, [setUser, navigate]);

  const toggleMobileMenu = useCallback(() => {
    setShowMobileMenu(prev => !prev);
  }, [setShowMobileMenu]);

  // Renderizar tickets según permisos granulados de reservas
  const renderTicketComponent = useCallback(() => {
    // Si es admin o admin de departamento, mostrar sistema completo
    if (isAdmin() || isAdminDepartamento) {
      return <TicketSystem user={user} />;
    }

    // Si tiene permisos granulados, verificar permisos avanzados de incidencias
    if (granularPermissions && granularPermissions.reservationPermissions) {
      const advancedPerms = granularPermissions.reservationPermissions.advancedPermissions || [];

      // Si tiene algún permiso avanzado relacionado con gestión, mostrar sistema completo
      const hasAdvancedAccess = advancedPerms.some(perm =>
        ['createSpaces', 'editSpaces', 'deleteSpaces', 'manageSpaces'].includes(perm)
      );

      if (hasAdvancedAccess) {
        return <TicketSystem user={user} />;
      }
    }

    // Por defecto, mostrar sistema de usuario
    return <TicketUserSystem user={user} />;
  }, [isAdmin, isAdminDepartamento, user, granularPermissions]);

  // Configuración de menú (memoizada)
  const menuItems = useMemo(() => [
    { id: 'inicio', name: 'Inicio', icon: <FaHome />, permission: null, component: 'inicio' },
    { id: 'gestion', name: 'Gestión', icon: <FaTools />, permission: 'gestion', component: 'gestion' },
    { id: 'correos', name: 'Correos', icon: <FaMailBulk />, permission: 'correos', component: 'correos' },
    { id: 'clases', name: 'Clases', icon: <FaChalkboardTeacher />, permission: 'clases', component: 'clases' },
    { id: 'reservas', name: 'Reservas', icon: <FaCalendarAlt />, permission: 'reservas', component: 'reservas' },
    { id: 'incidencias', name: 'Incidencias', icon: <FaTicketAlt />, permission: 'incidencias', component: 'incidencias' },
    { id: 'admin', name: 'Admin', icon: <FaUserShield />, permission: 'admin', component: 'admin' }
  ], []);

  const proximamenteItems = useMemo(() => [
    { id: 'comedor', name: 'Comedor (Próximamente)', icon: <FaUtensils />, feature: 'Comedor' },
    { id: 'rgpd', name: 'RGPD (Próximamente)', icon: <FaShieldAlt />, feature: 'RGPD' },
    { id: 'ia', name: 'IA (Próximamente)', icon: <FaRobot />, feature: 'IA' },
    { id: 'guardias', name: 'Sistema de Guardias (Próximamente)', icon: <FaClipboardCheck />, feature: 'Sistema de Guardias' }
  ], []);

  // Loading state
  if (permissionsLoading || roleLoading || granularLoading) {
    return null;
  }

  return (
    <div className={styles.appContainer}>
      {isMobile && showMobileMenu && (
        <div
          className={`${styles.overlay} ${showMobileMenu ? styles.visible : ''}`}
          onClick={toggleMobileMenu}
        />
      )}

      <div ref={sidebarRef}>
        <AppSidebar
          collapsed={collapsed}
          isMobile={isMobile}
          showMobileMenu={showMobileMenu}
          activeComponent={activeComponent}
          menuItems={menuItems}
          proximamenteItems={proximamenteItems}
          onToggleCollapse={toggleCollapsed}
          onNavigate={handleNavigate}
          onWebsiteClick={handleWebsiteClick}
          onProximamenteClick={handleProximamenteClick}
        />
      </div>

      <main className={styles.mainContent}>
        <div ref={topBarRef}>
          <TopBar
            title={componentTitle}
            collapsed={collapsed}
            isMobile={isMobile}
            showMobileMenu={showMobileMenu}
            onProfileClick={() => handleNavigate('perfil')}
            onLogout={handleLogout}
            onToggleMobileMenu={toggleMobileMenu}
          />
        </div>

        <div className={styles.componentContainer}>
          <div className={styles.contentWrapper}>
            <Routes>
              <Route path="/gestion" element={<ProtectedRoute permission="gestion"><Management /></ProtectedRoute>} />
              <Route path="/correos" element={<ProtectedRoute permission="correos"><Gmail user={user} userRole={userRole} granularPermissions={granularPermissions} /></ProtectedRoute>} />
              <Route path="/clases" element={<ProtectedRoute permission="clases"><ClassesComponent userRole={userRole} granularPermissions={granularPermissions} /></ProtectedRoute>} />
              <Route path="/reservas" element={<ProtectedRoute permission="reservas"><Reservas user={user} granularPermissions={granularPermissions} /></ProtectedRoute>} />
              <Route path="/incidencias" element={<ProtectedRoute permission="incidencias">{renderTicketComponent()}</ProtectedRoute>} />
              <Route path="/admin" element={<ProtectedRoute permission="admin"><AdminPanel /></ProtectedRoute>} />
              <Route path="/perfil" element={<ProfileComponent user={user} />} />
              <Route path="/inicio" element={<Inicio loggedIn={true} />} />
              <Route path="*" element={<Navigate to="/inicio" replace />} />
            </Routes>
          </div>

          <Footer collapsed={collapsed} isMobile={isMobile} />
        </div>
      </main>
    </div>
  );
};

// Componente principal sin BrowserRouter (ya existe en index.js)
const App = () => {
  const { user, setUser } = useContext(AuthContext);

  const handleLoginSuccess = useCallback((userData) => {
    localStorage.setItem('user', JSON.stringify(userData));
    if (userData.token) {
      localStorage.setItem('token', userData.token);
    }
    setUser(userData);
  }, [setUser]);

  if (!user) {
    return (
      <div className={styles.publicHomepage}>
        <Inicio onLoginSuccess={handleLoginSuccess} isPublic={true} />
      </div>
    );
  }

  return (
    <SocketProvider>
      <PermissionProvider user={user}>
        <AppContent />
      </PermissionProvider>
    </SocketProvider>
  );
};

export default App;